package fyi.moko.hotpl.ui.components

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import fyi.moko.hotpl.data.image.PhotoSyncStore
import fyi.moko.hotpl.ui.placeform.decodeImageBitmap
import org.koin.compose.koinInject

/** A resolved stored photo plus a way to ask again after [PhotoLoad.Unavailable]. */
class StoredPhotoHandle(
    val photo: PhotoLoad<ImageBitmap>,
    val retry: () -> Unit,
)

/**
 * Resolves a stored photo by file name: local first, then the account's photo store.
 * Null [fileName] is simply [PhotoLoad.Unavailable]. [StoredPhotoHandle.retry] re-runs the
 * whole resolution — the way back from Unavailable once the other device has uploaded.
 */
@Composable
fun rememberStoredPhoto(fileName: String?): StoredPhotoHandle {
    val store = koinInject<PhotoSyncStore>()
    var attempt by remember(fileName) { mutableIntStateOf(0) }
    val photo by produceState<PhotoLoad<ImageBitmap>>(
        initialValue = if (fileName == null) PhotoLoad.Unavailable else PhotoLoad.Loading,
        key1 = fileName,
        key2 = attempt,
    ) {
        if (fileName == null) return@produceState
        resolvePhotoLoad(store, fileName, ::decodeImageBitmap) { value = it }
    }
    return remember(photo, fileName) { StoredPhotoHandle(photo) { attempt++ } }
}

/**
 * A square photo tile that says what it's waiting for.
 *
 *  · Loading → plain surface (too brief to decorate).
 *  · Fetching → a small spinner: "coming from your other device".
 *  · Unavailable → a dimmed refresh glyph. With [retryOnTap] a tap asks again; without it
 *    (the edit form, where a tap means "remove") the glyph is informational only.
 *  · Lost → a warning glyph. Nothing to retry — this device dropped the bytes (0-18).
 *  · Loaded → the image.
 *
 * 스피너와 새로고침 글리프는 작고 흐리게 둔다 — 갤러리에서 사진보다 눈에 띄면 안 된다.
 */
@Composable
fun StoredPhotoTile(
    handle: StoredPhotoHandle,
    size: Dp,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    retryOnTap: Boolean = true,
    cornerRadius: Dp = 10.dp,
    /** Read for a loaded image; the waiting states describe themselves. */
    contentDescription: String? = null,
) {
    val photo = handle.photo
    val tapRetries = retryOnTap && photo is PhotoLoad.Unavailable
    Box(
        modifier = modifier
            .size(size)
            .clip(RoundedCornerShape(cornerRadius))
            .background(MaterialTheme.colorScheme.surfaceVariant)
            .clickable(enabled = enabled, onClick = if (tapRetries) handle.retry else onClick),
        contentAlignment = Alignment.Center,
    ) {
        when (photo) {
            PhotoLoad.Loading -> Unit
            PhotoLoad.Fetching -> CircularProgressIndicator(
                modifier = Modifier
                    .size(size / 4)
                    .semantics { this.contentDescription = "Downloading photo" },
                strokeWidth = 2.dp,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            PhotoLoad.Unavailable -> Icon(
                imageVector = Icons.Default.Refresh,
                contentDescription = if (tapRetries) "Photo not available yet. Tap to retry" else "Photo not available yet",
                tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.55f),
                modifier = Modifier.size(size / 3),
            )
            PhotoLoad.Lost -> Icon(
                imageVector = Icons.Default.Warning,
                contentDescription = "This photo is gone from this device",
                tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.55f),
                modifier = Modifier.size(size / 3),
            )
            is PhotoLoad.Loaded -> Image(
                bitmap = photo.value,
                contentDescription = contentDescription,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize(),
            )
        }
    }
}
